import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, ArrowRight, Check, KeyRound, Lock, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { BrandMark, BrandWordmark } from '../../../shared/components/BrandLogo';

const API = `${process.env.REACT_APP_API_URL || 'https://recruitai.pingitright.com/api'}/auth`;

// ── Palette ───────────────────────────────────────────────────────────────────
const c = {
  plum:       '#640D5F',
  rose:       '#D91656',
  grad:       'linear-gradient(135deg, #640D5F 0%, #D91656 100%)',
  gradSoft:   'linear-gradient(160deg, #FAF5FF 0%, #F5F5FB 100%)',
  surface:    '#FFFFFF',
  tintCool:   '#F5F5FB',
  tintWarm:   '#FAF5FF',
  border:     '#E6E2F0',
  borderSoft: '#EFEDF6',
  ink:        '#101828',
  inkDeep:    '#020842',
  muted:      '#49515B',
  mutedAlt:   '#3A3939',
  danger:     '#B42318',
  success:    '#0E7C51',
};

// ── Shared styles ─────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: c.gradSoft,
    fontFamily: 'Inter, system-ui, sans-serif', padding: 24,
  },
  card: {
    background: c.surface,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    padding: '40px 36px', maxWidth: 420, width: '100%',
    boxShadow: '0 18px 48px rgba(2, 8, 66, 0.08), 0 2px 6px rgba(2, 8, 66, 0.04)',
  },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: c.muted, marginBottom: 6 },
  input: {
    width: '100%', padding: '11px 14px', borderRadius: 8, fontSize: 14,
    background: c.tintCool, border: `1px solid ${c.border}`, color: c.ink,
    outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, background 0.15s',
  },
  btn: {
    width: '100%', padding: '12px', borderRadius: 8, fontSize: 14,
    fontWeight: 700, cursor: 'pointer', border: 'none', transition: 'all 0.15s',
  },
  btnPrimary: {
    background: c.grad, color: '#fff',
    boxShadow: '0 6px 16px rgba(217, 22, 86, 0.24)',
  },
  btnSecondary: {
    background: c.surface, color: c.plum,
    border: `1px solid ${c.border}`, marginTop: 8,
  },
  error: {
    background: 'rgba(180, 35, 24, 0.06)', border: '1px solid rgba(180, 35, 24, 0.22)',
    borderRadius: 8, padding: '10px 14px', fontSize: 13, color: c.danger, marginBottom: 16,
  },
  success: {
    background: 'rgba(14, 124, 81, 0.07)', border: '1px solid rgba(14, 124, 81, 0.22)',
    borderRadius: 8, padding: '10px 14px', fontSize: 13, color: c.success, marginBottom: 16,
  },
  title: { fontSize: 20, fontWeight: 700, color: c.inkDeep },
  subtitle: { fontSize: 13, color: c.mutedAlt, marginTop: 4 },
};

// ── Input field ───────────────────────────────────────────────────────────────
function Field({ label, type = 'text', value, onChange, placeholder, autoFocus }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={s.label}>{label}</label>
      <input
        style={s.input}
        type={type} value={value} onChange={onChange}
        placeholder={placeholder} autoFocus={autoFocus}
        onFocus={e => { e.target.style.borderColor = c.rose; e.target.style.background = c.surface; }}
        onBlur={e => { e.target.style.borderColor = c.border; e.target.style.background = c.tintCool; }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN LOGIN PAGE
// Tabs: Sign In | Create Account
// Inline forgot-password flow (3 steps)
// ─────────────────────────────────────────────────────────────────────────────
export default function Login() {
  const { user, loading, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlError = params.get('error');

  const [view, setView]     = useState('main');     // 'main' | 'forgot' | 'otp' | 'newpass'
  const [err, setErr]       = useState('');
  const [msg, setMsg]       = useState('');
  const [busy, setBusy]     = useState(false);

  // Sign-in form
  const [siEmail, setSiEmail]   = useState('');
  const [siPass,  setSiPass]    = useState('');


  // Forgot-password flow state
  const [fpEmail,  setFpEmail]  = useState('');
  const [fpOtp,    setFpOtp]    = useState('');
  const [fpToken,  setFpToken]  = useState('');
  const [fpPass,   setFpPass]   = useState('');
  const [fpPass2,  setFpPass2]  = useState('');

  const OAUTH_ERRS = {
    callback_failed:  'Login callback failed.',
    not_invited:      'No account exists for this address. Accounts are created by an administrator — please contact yours.',
    account_disabled: 'This account has been deactivated. Contact your administrator.',
  };

  useEffect(() => {
    if (!loading && user) navigate('/');
  }, [user, loading, navigate]);

  if (loading) return (
    <div style={{ ...s.page }}>
      <div className="spinner" style={{ width: 32, height: 32 }} />
    </div>
  );

  // ── Helper: store token and go to dashboard ───────────────────────────────
  const handleToken = async (token) => {
    await loginWithToken(token);
    navigate('/');
  };

  // ── Sign In ───────────────────────────────────────────────────────────────
  const handleSignIn = async (e) => {
    e.preventDefault();
    setErr('');

    // Validate locally first, so an empty field never comes back as
    // "Invalid email or password" from the server.
    const email = siEmail.trim();
    if (!email)                      { setErr('Please enter your email address.'); return; }
    if (!/^\S+@\S+\.\S+$/.test(email)) { setErr('Please enter a valid email address.'); return; }
    if (!siPass)                     { setErr('Please enter your password.'); return; }

    setBusy(true);
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password: siPass }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.detail || 'Login failed'); return; }
      await handleToken(data.token);
    } catch { setErr('Network error. Is the backend running?'); }
    finally { setBusy(false); }
  };

  // ── Forgot Password — Step 1: Send OTP ───────────────────────────────────
  const handleForgotSend = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API}/forgot-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fpEmail }),
      });
      if (!res.ok) { setErr('Request failed'); return; }
      setMsg(`OTP sent to ${fpEmail} — check your inbox (valid 10 min)`);
      setView('otp');
    } catch { setErr('Network error'); }
    finally { setBusy(false); }
  };

  // ── Forgot Password — Step 2: Verify OTP ─────────────────────────────────
  const handleOtpVerify = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    try {
      const res = await fetch(`${API}/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: fpEmail, otp: fpOtp }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.detail || 'Invalid OTP'); return; }
      setFpToken(data.reset_token);
      setMsg('OTP verified! Set your new password below.');
      setView('newpass');
    } catch { setErr('Network error'); }
    finally { setBusy(false); }
  };

  // ── Forgot Password — Step 3: Set New Password ────────────────────────────
  const handleResetPass = async (e) => {
    e.preventDefault();
    setErr('');
    if (fpPass !== fpPass2) { setErr('Passwords do not match'); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/reset-password`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reset_token: fpToken, new_password: fpPass }),
      });
      const data = await res.json();
      if (!res.ok) { setErr(data.detail || 'Reset failed'); return; }
      await handleToken(data.token);
    } catch { setErr('Network error'); }
    finally { setBusy(false); }
  };

  // ── Forgot Password Views ─────────────────────────────────────────────────
  if (view === 'forgot') return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center', color: c.plum }}><KeyRound size={32} strokeWidth={1.6} /></div>
          <div style={s.title}>Forgot Password</div>
          <div style={s.subtitle}>
            Enter your email — we'll send a 6-digit OTP
          </div>
        </div>
        {err && <div style={s.error}>{err}</div>}
        <form onSubmit={handleForgotSend}>
          <Field label="Email Address" type="email" value={fpEmail}
            onChange={e => setFpEmail(e.target.value)} placeholder="you@company.com" autoFocus />
          <button type="submit" style={{ ...s.btn, ...s.btnPrimary, marginTop: 8 }} disabled={busy}>
            {busy ? 'Sending OTP…' : <>Send OTP <ArrowRight size={14} strokeWidth={2.2} style={{ display: 'inline', verticalAlign: '-2px' }} /></>}
          </button>
        </form>
        <button type="button" style={{ ...s.btn, ...s.btnSecondary }}
          onClick={() => { setView('main'); setErr(''); }}>
          <ArrowLeft size={14} strokeWidth={2.2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Back to Sign In
        </button>
      </div>
    </div>
  );

  if (view === 'otp') return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center', color: c.plum }}><Mail size={32} strokeWidth={1.6} /></div>
          <div style={s.title}>Enter OTP</div>
          <div style={s.subtitle}>
            We sent a 6-digit code to <strong style={{ color: c.rose }}>{fpEmail}</strong>
          </div>
        </div>
        {msg && <div style={s.success}>{msg}</div>}
        {err && <div style={s.error}>{err}</div>}
        <form onSubmit={handleOtpVerify}>
          <div style={{ marginBottom: 16 }}>
            <label style={s.label}>6-Digit OTP</label>
            <input
              style={{
                ...s.input, textAlign: 'center', fontSize: 28,
                fontWeight: 700, letterSpacing: 12, fontFamily: 'monospace',
                color: c.inkDeep,
              }}
              type="text" inputMode="numeric" maxLength={6}
              value={fpOtp} onChange={e => setFpOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000" autoFocus
              onFocus={e => { e.target.style.borderColor = c.rose; e.target.style.background = c.surface; }}
              onBlur={e => { e.target.style.borderColor = c.border; e.target.style.background = c.tintCool; }}
            />
          </div>
          <button type="submit" style={{ ...s.btn, ...s.btnPrimary }} disabled={busy || fpOtp.length < 6}>
            {busy ? 'Verifying…' : <>Verify OTP <ArrowRight size={14} strokeWidth={2.2} style={{ display: 'inline', verticalAlign: '-2px' }} /></>}
          </button>
        </form>
        <button type="button" style={{ ...s.btn, ...s.btnSecondary }}
          onClick={() => { setFpOtp(''); setErr(''); setMsg(''); setView('forgot'); }}>
          <ArrowLeft size={14} strokeWidth={2.2} style={{ display: 'inline', verticalAlign: '-2px' }} /> Resend OTP
        </button>
      </div>
    </div>
  );

  if (view === 'newpass') return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ marginBottom: 6, display: 'flex', justifyContent: 'center', color: c.plum }}><Lock size={32} strokeWidth={1.6} /></div>
          <div style={s.title}>New Password</div>
          {msg && <div style={{ ...s.success, marginTop: 12 }}>{msg}</div>}
        </div>
        {err && <div style={s.error}>{err}</div>}
        <form onSubmit={handleResetPass}>
          <Field label="New Password" type="password" value={fpPass}
            onChange={e => setFpPass(e.target.value)} placeholder="At least 6 characters" autoFocus />
          <Field label="Confirm New Password" type="password" value={fpPass2}
            onChange={e => setFpPass2(e.target.value)} placeholder="Repeat password" />
          <button type="submit" style={{ ...s.btn, ...s.btnPrimary, marginTop: 4 }} disabled={busy}>
            {busy ? 'Saving…' : <><Check size={14} strokeWidth={2.5} style={{ display: 'inline', verticalAlign: '-2px' }} /> Set New Password & Log In</>}
          </button>
        </form>
      </div>
    </div>
  );

  // ── Main Login / Register view ────────────────────────────────────────────
  return (
    <div style={s.page}>
      <div style={s.card}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ marginBottom: 10 }}>
            <BrandMark size={52} />
          </div>
          <BrandWordmark size={22} weight={800} color={c.inkDeep} style={{ display: 'block' }} />
          <div style={{ fontSize: 12, color: c.muted }}>by Amartam Solutions</div>
        </div>

        {/* OAuth error */}
        {urlError && (
          <div style={s.error}>
            <AlertTriangle size={14} strokeWidth={2} style={{ display: 'inline', verticalAlign: '-2px' }} /> {({
              callback_failed: 'Callback failed. Please try again.',
              not_invited: 'No account exists for this address. Accounts are created by an administrator — please contact yours.',
              account_disabled: 'This account has been deactivated. Contact your administrator.',
            })[urlError] || 'Login failed. Please try again.'}
          </div>
        )}

        {err && <div style={s.error}>{err}</div>}

        {/* ── SIGN IN ── (no self-registration: accounts are created by an admin) */}
        <form onSubmit={handleSignIn}>
          <Field label="Email" type="email" value={siEmail}
            onChange={e => setSiEmail(e.target.value)} placeholder="you@company.com" autoFocus />
          <Field label="Password" type="password" value={siPass}
            onChange={e => setSiPass(e.target.value)} placeholder="Enter Your Password" />
          <div style={{ textAlign: 'right', marginTop: -8, marginBottom: 14 }}>
            <button type="button"
              style={{ background: 'none', border: 'none', fontSize: 12, color: c.rose, fontWeight: 600, cursor: 'pointer' }}
              onClick={() => { setFpEmail(siEmail); setView('forgot'); setErr(''); }}>
              Forgot password?
            </button>
          </div>
          <button type="submit" style={{ ...s.btn, ...s.btnPrimary }} disabled={busy}>
            {busy ? 'Signing in…' : <>Sign In <ArrowRight size={14} strokeWidth={2.2} style={{ display: 'inline', verticalAlign: '-2px' }} /></>}
          </button>
        </form>

        {/* Candidate portal hidden — applicants join from email links only
        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 12, color: c.mutedAlt }}>
          Are you a job applicant?{' '}
          <a href="/portal" style={{ color: c.plum, fontWeight: 600, textDecoration: 'none' }}>Candidate Portal <ArrowRight size={12} strokeWidth={2.2} style={{ display: 'inline', verticalAlign: '-2px' }} /></a>
        </div>
        */}
      </div>
    </div>
  );
}